// src/utils/api.js
import axios from 'axios';

const api = axios.create({
  // Prod by default; set REACT_APP_API_URL to point at staging (e.g. the CuoreCare
  // staging backend) when testing feature-branch endpoints.
  baseURL: process.env.REACT_APP_API_URL || 'https://cuore-backend-995g.onrender.com',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 🔐 Inject token only for protected routes
api.interceptors.request.use(
  config => {
    // Let axios set Content-Type (with boundary) when sending FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    const publicRoutes = [
      '/api/web/auth/send-login-otp',
      '/api/web/auth/verify-otp',
    ];

    // Normalize URL check (handles relative vs absolute)
    const isPublic = publicRoutes.some(route => config.url?.includes(route));

    if (!isPublic) {
      const rawtoken = localStorage.getItem('token');
      const token = rawtoken?.replace(/^"|"$/g, '');
      if (token) {
        // Use bracket notation to avoid casing issues
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Never log the token or the request config here: it would put the bearer
    // token and request bodies (PHI, for CuoreCare) into the browser console,
    // where screen recorders and error reporters pick them up.
    return config;
  },
  error => Promise.reject(error)
);

// 🚫 Global 401 handler for session expiration.
//
// Deliberately 401 ONLY, not 403: 401 means the token itself is invalid/expired (the
// session is genuinely dead — log out). 403 means the token is fine but this doctor isn't
// authorized for THIS specific resource (e.g. a CuoreCare-only endpoint for a non-CuoreCare
// doctor, or a patient outside their linked list) — that's an expected, per-request
// business rule, not a dead session, and the calling code (e.g. ConsultationHistory's own
// "hide myself on 403" handling) needs the chance to react instead of the whole app being
// yanked to /login out from under it.
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const wasOrgAdmin = localStorage.getItem('role') === 'org_admin';
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      // Only redirect if not already on login page. A clinic/corporate admin goes back to
      // their own sign-in, not the doctor OTP page.
      if (!window.location.pathname.includes('/login')) {
        window.location.href = wasOrgAdmin ? '/org/login' : '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Generic methods
// `config` is merged in alongside `params` — optional, used e.g. for
// `{ responseType: 'blob' }` when a GET returns a rendered PDF instead of JSON.
const get = (url, params = {}, config = {}) => api.get(url, { params, ...config });
const post = (url, data = {}, config = {}) => api.post(url, data, config);
const put = (url, data = {}, config = {}) => api.put(url, data, config);
const del = (url, config = {}) => api.delete(url, config);
const patch = (url, data = {}, config = {}) => api.patch(url, data, config);

const setToken = token => {
  api.defaults.headers['Authorization'] = `Bearer ${token}`;
  localStorage.setItem('token', token);
};

const apiMethods = { get, post, put, patch, delete: del, setToken };

export default apiMethods;
